/*
 * lab-contact.js — DIRECTION.md §5.11 and §4 "Town map" (group E).
 *
 * <lab-contact-form>  wraps {% form 'contact' %}: preselects the reason from ?reason=
 *                     (the services pages link here with ?reason=molitura|imbottigliamento|sfuso,
 *                     the lot page with ?reason=ordine&lot=…), and prefills the message with
 *                     the lot code when ?lot= is present and the message is empty.
 * <lab-map-embed>     the consent-gated Google iframe behind "Mostra la mappa": loads only
 *                     when window.cookieConsentState.marketingAllowed is true; otherwise it
 *                     opens the consent preferences and waits for visitorConsentCollected.
 *
 * Both read state in connectedCallback and remove their listeners with an AbortController.
 */
(() => {
  const REASON_ALIASES = {
    order: 'ordine',
    ordine: 'ordine',
    sfuso: 'ordine',
    bulk: 'ordine',
    molitura: 'molitura',
    milling: 'molitura',
    imbottigliamento: 'imbottigliamento',
    bottling: 'imbottigliamento',
    visita: 'visita',
    visit: 'visita',
    rivenditori: 'rivenditori',
    resellers: 'rivenditori',
    altro: 'altro',
    other: 'altro',
  };

  const readParams = () => {
    try {
      return new URLSearchParams(window.location.search);
    } catch (error) {
      return new URLSearchParams();
    }
  };

  if (!customElements.get('lab-contact-form')) {
    class LabContactForm extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        const params = readParams();
        this.preselectReason(params.get('reason'));
        this.prefillLot(params.get('lot'));
      }

      disconnectedCallback() {
        if (this.abort) this.abort.abort();
      }

      preselectReason(raw) {
        const select = this.querySelector('select[data-reason-select]');
        if (!select || !raw) return;
        const wanted = REASON_ALIASES[String(raw).trim().toLowerCase()];
        if (!wanted) return;
        const option = Array.from(select.options).find((item) => item.dataset.reason === wanted);
        if (!option) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      prefillLot(raw) {
        const textarea = this.querySelector('textarea[data-message]');
        if (!textarea || !raw) return;
        const code = String(raw).trim().toUpperCase();
        if (!code || textarea.value.trim() !== '') return;
        const prefix = textarea.dataset.lotLine || '';
        textarea.value = prefix ? `${prefix} ${code}\n` : `${code}\n`;
      }
    }
    customElements.define('lab-contact-form', LabContactForm);
  }

  if (!customElements.get('lab-map-embed')) {
    class LabMapEmbed extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        const { signal } = this.abort;
        this.button = this.querySelector('[data-map-show]');
        this.note = this.querySelector('[data-map-note]');
        this.frameHost = this.querySelector('[data-map-frame]');
        if (!this.button || !this.frameHost) return;
        this.button.addEventListener('click', () => this.request(), { signal });
        document.addEventListener(
          'visitorConsentCollected',
          (event) => {
            if (!this.pending) return;
            const detail = (event && event.detail) || {};
            if (detail.marketingAllowed) {
              this.load();
            } else {
              this.explain();
            }
          },
          { signal }
        );
      }

      disconnectedCallback() {
        if (this.abort) this.abort.abort();
      }

      marketingAllowed() {
        const state = window.cookieConsentState;
        return Boolean(state && state.marketingAllowed);
      }

      request() {
        if (this.dataset.state === 'loaded') return;
        if (this.marketingAllowed()) {
          this.load();
          return;
        }
        this.pending = true;
        this.explain();
        if (typeof window.showCookiePreferences === 'function') {
          window.showCookiePreferences();
        }
      }

      explain() {
        if (this.note) this.note.textContent = this.dataset.consentNote || '';
      }

      load() {
        this.pending = false;
        if (this.dataset.state === 'loaded') return;
        const src = this.dataset.src;
        if (!src) return;
        const frame = document.createElement('iframe');
        frame.className = 'tmap__frame';
        frame.src = src;
        frame.title = this.dataset.title || '';
        frame.loading = 'lazy';
        frame.referrerPolicy = 'no-referrer-when-downgrade';
        frame.setAttribute('allowfullscreen', '');
        this.frameHost.replaceChildren(frame);
        this.dataset.state = 'loaded';
        if (this.note) this.note.textContent = '';
        frame.focus({ preventScroll: true });
      }
    }
    customElements.define('lab-map-embed', LabMapEmbed);
  }
})();
